// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Clickable from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import XngCarousel, { SwiperStyle } from "@tps/App/Components/XngCarousel";
import { BRAND_GALLERY_SLIDES_PER_SCREENSIZE, SLIDES_PER_VIEW_POINTS } from "@tps/XDL/constants";
import { generateSlidesPerViewMap } from "@tps/XDL/Utilities";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    brandsToDisplay = "brandsToDisplay",
    numberOfBrandsToDisplay = "numberOfBrandsToDisplay",
    brandsSelected = "brandsSelected",
    maximumNumberOfBrandsPerRow = "maximumNumberOfBrandsPerRow",
    maximumBrandImageHeight = "maximumBrandImageHeight",
    // XNG-Changes
    fadeEffectWidth = "fadeEffectWidth",
    fadeEffectColor = "fadeEffectColor",
    viewType = "viewType",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.brandsToDisplay]: "random" | "select-brands";
        [fields.numberOfBrandsToDisplay]: string;
        [fields.brandsSelected]: string[];
        [fields.maximumNumberOfBrandsPerRow]: string;
        [fields.maximumBrandImageHeight]: number;
        // XNG-Changes: add extra fields for fade effect
        [fields.fadeEffectWidth]: number;
        [fields.fadeEffectColor]: string;
        [fields.viewType]: "carousel" | "gallery";
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: props.fields.brandsSelected,
        randomize: props.fields.brandsToDisplay === "random",
        pageSize: parseInt(props.fields.numberOfBrandsToDisplay || "0", 10),
        select: "id,name,detailPagePath,logoSmallImagePath,logoAltText",
    };
    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    const shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;

    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandListStyles {
    titleText?: TypographyProps;
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
    skipToContent?: SkipNavStyles;
    // XNG-Changes
    carouselWrapper?: InjectableCss;
    carouselStyles?: SwiperStyle;
    carouselExtendedStyles?: SwiperStyle;
    brandName?: TypographyPresentationProps;
    imageWrapper?: InjectableCss;
}

export const listStyles: BrandListStyles = {
    titleText: {
        variant: "h3",
        transform: "inherit",
        css: css`
            border-width: 20px;
            text-align: center;
        `,
    },
    imageItem: {
        css: css`
            justify-content: center;
        `,
    },
};

const styles = listStyles;

class BrandGallery extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const { loadBrandGallery, shouldLoadBrandGallery, brandGalleryParameter } = this.props;
        if (shouldLoadBrandGallery) {
            loadBrandGallery(brandGalleryParameter);
        }
    }

    render() {
        const {
            brandsState: { isLoading, value: brandList },
            fields,
        } = this.props;

        if (isLoading || !brandList) {
            return null;
        }

        const desktop = parseInt(fields.maximumNumberOfBrandsPerRow, 10);
        const tablet = Math.ceil(desktop / 2);
        const mobile = Math.ceil(tablet / 2);

        const imgProps = {
            style: {
                width: "auto",
                maxWidth: "100%",
                maxHeight: `${fields.maximumBrandImageHeight}px`,
            },
        };

        const focusFunction = () => {
            const elementToFocus = document.getElementById("collapseButton");
            if (elementToFocus) {
                elementToFocus.focus();
            } else {
                document.getElementById("expandButton")?.focus();
            }
        };

        // <XNG-Changes>
        const renderBrands = brandList.map(brand => (
            <StyledWrapper key={brand.id} {...styles.imageItem} data-test-selector="brands-image">
                {brand === brandList[0] && (
                    <SkipNav
                        extendedStyles={styles.skipToContent}
                        text={translate("Skip to full brand list")}
                        focusFunction={focusFunction}
                    />
                )}
                <Clickable href={brand.detailPagePath} {...styles.link} aria-label="Go to Brand Page">
                    <LazyImage
                        imgProps={imgProps}
                        {...styles.image}
                        src={brand.logoSmallImagePath}
                        altText={`${translate("Go to brand page")} ${brand.logoAltText || brand.name}`}
                    />
                </Clickable>
            </StyledWrapper>
        ));

        const breakpoints = generateSlidesPerViewMap({
            [SLIDES_PER_VIEW_POINTS.xs]: BRAND_GALLERY_SLIDES_PER_SCREENSIZE.mobile,
            [SLIDES_PER_VIEW_POINTS.sm]: BRAND_GALLERY_SLIDES_PER_SCREENSIZE.tablet,
            [SLIDES_PER_VIEW_POINTS.md]: BRAND_GALLERY_SLIDES_PER_SCREENSIZE.desktop,
            [SLIDES_PER_VIEW_POINTS.lg]: parseInt(fields.maximumNumberOfBrandsPerRow, 10),
        });
        // </XNG-Changes>

        return (
            <StyledWrapper
                {...styles.container}
                data-test-selector="brandGallery"
                className={this.props.fields.viewType === "carousel" ? "carousel-brands" : "gallary-brands"}
            >
                <VisuallyHidden as="span">{translate("Explore available brands")}</VisuallyHidden>
                {/* XNG-Changes: remove attached variant */}
                {this.props.fields.title && (
                    <Typography
                        {...styles.titleText}
                        data-test-selector="brandGalleryTitle"
                        {...{ viewType: this.props.fields.viewType }}
                    >
                        {this.props.fields.title}
                    </Typography>
                )}

                {/* XNG-Changes: use carousel */}
                {this.props.fields.viewType === "carousel" ? (
                    <StyledWrapper
                        {...styles.carouselWrapper}
                        {...{
                            fadeEffectWidth: fields.fadeEffectWidth || 100,
                            fadeEffectColor: fields.fadeEffectColor || "#FFF",
                        }}
                    >
                        <XngCarousel
                            spaceBetween={25}
                            slidesPerView={BRAND_GALLERY_SLIDES_PER_SCREENSIZE.mobile}
                            scrollbar={false}
                            navigation={false}
                            breakpoints={breakpoints}
                            centerInsufficientSlides={false}
                            autoplay={true}
                            extendedStyles={styles.carouselExtendedStyles}
                        >
                            {renderBrands}
                        </XngCarousel>
                    </StyledWrapper>
                ) : (
                    <GridContainer gap={20}>
                        {brandList.map(brand => (
                            <GridItem key={brand.id} width={[6, 4, 3, 2, 2]}>
                                <Clickable aria-label={translate("Go to brand page")} href={brand.detailPagePath} {...styles.imageWrapper}>
                                    <LazyImage
                                        {...styles.image}
                                        src={brand.logoSmallImagePath}
                                        altText={`${translate("Go to brand page")} ${brand.logoAltText || brand.name}`}
                                    />
                                    <Typography {...styles.brandName}>{brand.name}</Typography>
                                </Clickable>
                            </GridItem>
                        ))}
                    </GridContainer>
                )}
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Fade Effect Settings",
    sortOrder: 1,
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandGallery),
    definition: {
        group: "Brands",
        icon: "image",
        displayName: "Brand Gallery",
        allowedContexts: ["BrandsPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            // XNG-Change add the view type option
            {
                name: fields.viewType,
                displayName: "View Type",
                editorTemplate: "DropDownField",
                defaultValue: "carousel",
                options: [
                    { displayName: "Carousel View", value: "carousel" },
                    { displayName: "Gallery View", value: "gallery" },
                ],
                tooltip: "We use the gallery view in the brands list page",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.brandsToDisplay,
                displayName: "Brands to display",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Random",
                        value: "random",
                    },
                    {
                        displayName: "Select brands",
                        value: "select-brands",
                    },
                ],
                defaultValue: "random",
                horizontal: true,
                fieldType: "Contextual",
                tooltip:
                    "Brand logos can be picked at random on page load, or you can select specific brands to display.",
                tab: basicTab,
            },
            {
                name: fields.numberOfBrandsToDisplay,
                displayName: "Number of brands to display",
                editorTemplate: "DropDownField",
                defaultValue: "12",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                    { displayName: "9", value: "9" },
                    { displayName: "10", value: "10" },
                    { displayName: "11", value: "11" },
                    { displayName: "12", value: "12" },
                    { displayName: "13", value: "13" },
                    { displayName: "14", value: "14" },
                    { displayName: "15", value: "15" },
                    { displayName: "16", value: "16" },
                    { displayName: "17", value: "17" },
                    { displayName: "18", value: "18" },
                ],
                fieldType: "Contextual",
                isVisible: item => item.fields.brandsToDisplay === "random",
                tab: basicTab,
            },
            {
                name: fields.brandsSelected,
                displayName: "Select brands",
                editorTemplate: "SelectBrandsField",
                isVisible: item => item.fields.brandsToDisplay === "select-brands",
                defaultValue: [],
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.maximumNumberOfBrandsPerRow,
                displayName: "Maximum brands per row",
                editorTemplate: "DropDownField",
                defaultValue: "6",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                ],
                fieldType: "Contextual",
                tooltip:
                    "Up to 6 brands can display per row. This number should be adjusted based on the number of brands you choose to display. For example, if you display 10 brands you likely want 5 to display per row. Logos will also stack responsively for smaller screen sizes.",
                tooltipPosition: "left",
                tab: basicTab,
                isVisible: item => item?.fields.viewType === "carousel",
            },
            {
                name: fields.maximumBrandImageHeight,
                displayName: "Maximum brand image height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "Contextual",
                tooltip:
                    "By default, images will scale to meet the width of the screen for the number of brands to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
                validate: (value, item) => {
                    return value && value > 100 ? translate("Maximum Image height is 100px.") : null;
                },
                tab: basicTab,
                isVisible: item => item?.fields.viewType === "carousel",
            },
            {
                name: fields.fadeEffectWidth,
                displayName: "Fade Effect Width in Pixels",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "Contextual",
                tab: advancedTab,
                isVisible: item => item?.fields.viewType === "carousel",
            },
            {
                name: fields.fadeEffectColor,
                displayName: "Fade Effect Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#FFFFFF",
                fieldType: "Contextual",
                tab: advancedTab,
                isVisible: item => item?.fields.viewType === "carousel",
            },
        ],
    },
};

export default widgetModule;
